/* =========================================================================
   ClueVine — Today's garden
   A compact board of every enabled mode's daily for today: a bloom for a win,
   a wilted stem for a loss, a sprout for one in progress, a seed for one not
   yet played. Answers "what's left today?" at a glance, and shares the whole
   day in one message.
   ========================================================================= */

import type { AnyPuzzleMode } from '../engine/types';
import { useUntilNextDaily } from '../hooks/useToday';
import type { Garden } from '../state/garden';
import { accentVars } from '../utils/accent';
import { formatClock } from '../utils/format';
import { Icon } from './Icon';
import { ProgressRing } from './ProgressRing';

const PLOT_LABEL = { won: 'solved', lost: 'missed', open: 'not played yet' } as const;

function Countdown() {
  const ms = useUntilNextDaily();
  return <span className="cv-mono">{formatClock(ms)}</span>;
}

export function TodaysGarden({
  garden,
  streak,
  atRisk,
  onPick,
  onShare,
}: {
  garden: Garden;
  streak: number;
  atRisk: boolean;
  onPick: (mode: AnyPuzzleMode) => void;
  onShare: () => void;
}) {
  const { won, played, total, complete } = garden;

  return (
    <section className="cv-card cv-garden" aria-labelledby="cv-garden-title">
      <div className="cv-garden__head">
        <ProgressRing value={played} max={total} size={54} stroke={6} ariaLabel={`${played} of ${total} dailies played`}>
          <span className="cv-mono cv-garden__count">
            {played}/{total}
          </span>
        </ProgressRing>
        <div className="cv-garden__text">
          <span className="cv-eyebrow" id="cv-garden-title">
            Today’s garden · #{garden.day}
          </span>
          <span className="cv-garden__status">
            {complete
              ? `All ${total} tended — ${won} in bloom`
              : played === 0
                ? atRisk
                  ? `Play any daily to keep your ${streak}-day streak`
                  : 'Every mode has a fresh daily'
                : `${won} in bloom · ${total - played} to go`}
          </span>
          <span className="cv-faint cv-garden__next">
            New dailies in <Countdown />
          </span>
        </div>
      </div>

      <ul className="cv-garden__plots" aria-label="Today’s dailies">
        {garden.entries.map((e) => {
          const state = e.growing ? 'growing' : e.plot;
          return (
            <li key={e.mode.id}>
              <button
                type="button"
                className="cv-garden__plot"
                data-plot={state}
                style={accentVars(e.mode.accent)}
                onClick={() => onPick(e.mode)}
                aria-label={`${e.mode.name}: ${e.growing ? 'in progress' : PLOT_LABEL[e.plot]}`}
                title={e.mode.name}
              >
                <Icon name={e.mode.glyph} size={18} />
                <span className="cv-garden__mark" aria-hidden="true">
                  {state === 'won' ? '✓' : state === 'lost' ? '✕' : state === 'growing' ? '•' : ''}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {played > 0 ? (
        <button type="button" className="cv-result__copylink cv-garden__share" onClick={onShare}>
          <Icon name="share" size={14} /> Share today’s garden
        </button>
      ) : null}
    </section>
  );
}
